import { ComputerDesktopIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isTrialMode, TRIAL_RECEIVER_HINT } from '../../../auth/trial-session';
import { CompanionCard } from '../../../components/companion-card';
import { EmptyState } from '../../../components/empty-state';
import { AlertContext } from '../../../contexts/alert-context';
import { makeCompanionBusyPredicate, type ReceivingSession } from '../../../data/receiving';
import type { Companion, UploaderConnection } from '../../../data/uploader-connection';
import { receivingSessionsQueryOptions } from '../../../query/query-options';
import type { ReceivingService } from '../../../services/receiving';
import { StepHeading } from './step-heading';

type CompanionPickerStepProps = {
  connections: UploaderConnection[];
  storeId: string;
  sessionId: string;
  disabled: boolean;
  service: ReceivingService;
};

export function CompanionPickerStep({
  connections,
  storeId,
  sessionId,
  disabled,
  service,
}: CompanionPickerStepProps) {
  const [connecting, setConnecting] = useState<string | null>(null);
  const { addErrorAlert } = useContext(AlertContext);

  const { data: sessions = [] } = useQuery<ReceivingSession[]>({
    ...receivingSessionsQueryOptions(storeId),
    queryFn: () => service.listActiveSessions(storeId),
  });

  const isCompanionBusy = makeCompanionBusyPredicate(sessions);

  const companions = connections.filter((c): c is Companion => Boolean(c.uploader?.machine_id));

  const ordered = companions
    .map((conn) => ({
      conn,
      busy: isCompanionBusy(conn),
    }))
    .sort((a, b) => Number(a.busy) - Number(b.busy));

  const handlePick = useCallback(
    (machineId: string) => {
      if (disabled || connecting) {
        return;
      }

      setConnecting(machineId);
      void service.setCompanion(storeId, sessionId, machineId).catch((err: unknown) => {
        setConnecting(null);

        // Surface the backend's reason (e.g. "companion already has an active
        // receiving session") instead of silently no-op'ing the click. The
        // fetch wrapper prefixes thrown errors with "Invalid response:" — strip
        // it so the toast reads as a plain sentence for the store owner.
        const reason = err instanceof Error ? err.message.replace(/^Invalid response:\s*/, '') : '';
        addErrorAlert(
          reason
            ? reason.charAt(0).toUpperCase() + reason.slice(1)
            : "Couldn't select this companion. Please refresh and try again.",
          { cause: err }
        );
      });
    },
    [disabled, connecting, service, storeId, sessionId, addErrorAlert]
  );

  // With exactly one Companion computer there's no ambiguity about which machine
  // will receive, so skip the click and advance straight to Vendor. With two or
  // more we must NOT auto-pick — the owner has to consciously choose the computer
  // they'll walk to for LiquorPOS. Guarded by a ref so re-renders can't double-fire.
  //
  // Never auto-pick once we've seen more than one Companion: with 2+ the owner
  // must consciously choose the machine they'll walk to, and a transient
  // WebSocket drop (2 -> 1) must not silently auto-select the survivor.
  const sawMultipleRef = useRef(false);
  const autoAdvancedRef = useRef(false);
  useEffect(() => {
    if (companions.length >= 2) {
      sawMultipleRef.current = true;

      return;
    }

    if (disabled || autoAdvancedRef.current || sawMultipleRef.current) {
      return;
    }

    if (companions.length !== 1) {
      return;
    }

    const only = ordered[0];
    if (!only || only.busy) {
      return;
    }

    autoAdvancedRef.current = true;
    handlePick(only.conn.uploader.machine_id);
  }, [companions.length, disabled, ordered, handlePick]);

  if (ordered.length === 0) {
    // A trial receives through the capture exe (ORD-536), not a Companion.
    const trial = isTrialMode();

    return (
      <EmptyState
        icon={<ComputerDesktopIcon className="size-8" />}
        title={trial ? "Orderbot Demo isn't open" : 'No Companion computers online'}
        description={
          trial
            ? TRIAL_RECEIVER_HINT
            : "Open the Orderbot Companion app on one of your store's computers to continue."
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 min-h-0">
      <StepHeading>Select a Companion</StepHeading>
      {ordered.length > 1 && (
        <p className="text-base leading-relaxed text-gray-700">
          Choose the store computer that will receive this order: you'll finish the receiving in
          LiquorPOS on that computer.
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        {ordered.map(({ conn, busy }) => {
          const isConnecting = connecting === conn.uploader.machine_id;

          return (
            <CompanionCard
              key={conn.connection_id}
              connection={conn}
              busy={busy}
              isActive={isConnecting}
              onClick={
                !busy && !disabled && !connecting
                  ? () => handlePick(conn.uploader.machine_id)
                  : undefined
              }
            />
          );
        })}
      </div>
    </div>
  );
}
